import React, { useState } from 'react';
import { authFetch } from './authFetch';

// Use environment variable for API URL, with fallback
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://mfacrm.onrender.com';

// Production mode check - disable verbose logging in production
const IS_DEV = process.env.NODE_ENV === 'development';

// Safe logging utility - only logs in development
const devLog = (...args) => {
    if (IS_DEV) {
        console.log(...args);
    }
};

const ScanButton = ({ customerId, customerName }) => {
    const [error, setError] = useState(null);
    const [status, setStatus] = useState('ready'); // ready, checking, success, failed

    /**
     * Get or create a scanner API key for the current user.
     * Keys are stored in localStorage and auto-created if needed.
     */
    const getScannerApiKey = async () => {
        // Check if we have a stored key
        const storedKey = localStorage.getItem('scannerApiKey');

        if (storedKey) {
            // Verify the key is still valid
            // Use regular fetch (not authFetch) since this endpoint uses X-Scanner-API-Key
            // header authentication, not session auth. authFetch would redirect to /login
            // on 401, but a 401 here just means the scanner key expired, not the user session.
            try {
                const verifyResponse = await fetch(`${API_BASE_URL}/api/scanner/verify/`, {
                    headers: {
                        'X-Scanner-API-Key': storedKey
                    }
                });

                if (verifyResponse.ok) {
                    devLog('Existing scanner key is valid');
                    return storedKey;
                }

                // Key is invalid (expired, revoked, or timed out)
                devLog('Stored scanner key is no longer valid');
                localStorage.removeItem('scannerApiKey');
            } catch (e) {
                devLog('Error verifying scanner key:', e);
                localStorage.removeItem('scannerApiKey');
            }
        }

        // No valid key - create a new one automatically
        devLog('Creating new scanner API key');
        try {
            const createResponse = await authFetch(`${API_BASE_URL}/api/scanner/keys/create/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ name: 'Auto-created Scanner Key' })
            });

            if (!createResponse.ok) {
                throw new Error('Failed to create scanner key');
            }

            const data = await createResponse.json();
            localStorage.setItem('scannerApiKey', data.key);
            devLog('New scanner key created and stored');
            return data.key;
        } catch (e) {
            devLog('Error creating scanner key:', e);
            throw new Error('Could not obtain scanner credentials. Please try again.');
        }
    };

    const detectProtocolHandler = (protocolUrl) => {
        return new Promise((resolve) => {
            let detected = false;
            let timeoutId = null;
            let blurTimeoutId = null;
            const startTime = Date.now();

            const cleanup = () => {
                window.removeEventListener('blur', handleBlur);
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                if (timeoutId) clearTimeout(timeoutId);
                if (blurTimeoutId) clearTimeout(blurTimeoutId);
            };

            const onDetected = () => {
                if (!detected) {
                    detected = true;
                    devLog('Scanner detected');
                    cleanup();
                    resolve(true);
                }
            };

            const onNotDetected = () => {
                if (!detected) {
                    detected = true;
                    devLog('Scanner not detected');
                    cleanup();
                    resolve(false);
                }
            };

            // Method 1: Window blur detection
            const handleBlur = () => {
                onDetected();
            };

            // Method 2: Visibility change detection
            const handleVisibilityChange = () => {
                if (document.hidden) {
                    onDetected();
                }
            };

            // Set up listeners
            window.addEventListener('blur', handleBlur);
            document.addEventListener('visibilitychange', handleVisibilityChange);

            // Method 3: Try opening in hidden iframe first (more reliable on some browsers)
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = protocolUrl;
            document.body.appendChild(iframe);

            // Clean up iframe after short delay
            setTimeout(() => {
                if (iframe.parentNode) {
                    document.body.removeChild(iframe);
                }
            }, 500);

            // Small delay before checking main window
            blurTimeoutId = setTimeout(() => {
                devLog('Attempting protocol launch');
                try {
                    window.location.href = protocolUrl;
                } catch (e) {
                    devLog('Error opening protocol');
                    onNotDetected();
                }
            }, 100);

            // Give it 2.5 seconds to detect
            timeoutId = setTimeout(() => {
                onNotDetected();
            }, 2500);
        });
    };

    const handleScanClick = async () => {
        devLog('Scan button clicked');
        setError(null);
        setStatus('ready');

        // Validate props
        if (!customerId) {
            setError('Customer information is missing. Please refresh the page.');
            return;
        }

        if (!customerName) {
            setError('Customer information is missing. Please refresh the page.');
            return;
        }

        // Get username from localStorage
        let username = '';
        try {
            const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
            username = userDetails.username || userDetails.email || '';
        } catch (e) {
            devLog('Could not parse userDetails');
        }

        devLog('Preparing scanner data');
        setStatus('checking');

        try {
            // Get or create a scanner API key (auto-managed)
            const scannerApiKey = await getScannerApiKey();

            // Create the data object with scanner API key
            // Using dedicated scanner API key instead of user token for better security
            const scanData = {
                customerId: parseInt(customerId),
                customerName: String(customerName),
                scannerApiKey: scannerApiKey,  // New: dedicated scanner API key
                apiUrl: API_BASE_URL,
                username: username,
                version: '2.0' // Version 2.0 uses scanner API keys
            };

            // Convert to JSON and encode
            const jsonString = JSON.stringify(scanData);
            const base64Data = btoa(jsonString);
            const protocolUrl = `mfacrm://scan?data=${encodeURIComponent(base64Data)}`;

            devLog('Protocol URL prepared');

            // Detect if scanner opens
            const scannerOpened = await detectProtocolHandler(protocolUrl);

            if (scannerOpened) {
                devLog('Scanner app opened successfully');
                setStatus('success');

                // Reset to ready after 2 seconds
                setTimeout(() => setStatus('ready'), 2000);
            } else {
                devLog('Scanner app did not open');
                setStatus('failed');

                // Show user-friendly prompt
                setTimeout(() => {
                    const userWantsSetup = window.confirm(
                        'Scanner App Not Found\n\n' +
                        'The MFACRM Scanner does not appear to be installed on this computer.\n\n' +
                        'Click OK to download and install it now\n' +
                        'Click Cancel if the scanner is already open\n\n' +
                        'Installation takes about 2 minutes.'
                    );

                    if (userWantsSetup) {
                        window.location.href = 'https://crm.midfloridaagencies.com/ScannerSetup.html';
                    } else {
                        setStatus('ready');
                    }
                }, 100);
            }

        } catch (error) {
            devLog('Error in scanner launch');
            setError('Unable to launch scanner. Please try again.');
            setStatus('ready');
        }
    };

    const getButtonStyle = () => {
        const baseStyle = {
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            fontSize: '16px',
            borderRadius: '4px',
            marginRight: '10px',
            transition: 'all 0.3s ease'
        };

        switch (status) {
            case 'checking':
                return {
                    ...baseStyle,
                    backgroundColor: '#ffc107',
                    cursor: 'wait',
                };
            case 'success':
                return {
                    ...baseStyle,
                    backgroundColor: '#28a745',
                    cursor: 'default',
                };
            case 'failed':
                return {
                    ...baseStyle,
                    backgroundColor: '#dc3545',
                    cursor: 'pointer',
                };
            default: // ready
                return {
                    ...baseStyle,
                    backgroundColor: '#007bff',
                    cursor: 'pointer',
                };
        }
    };

    const getButtonText = () => {
        switch (status) {
            case 'checking':
                return '⏳ Opening Scanner...';
            case 'success':
                return '✓ Scanner Opened!';
            case 'failed':
                return '⚠️ Scanner Not Found';
            default:
                return '📄 Scan Document';
        }
    };

    return (
        <div style={{ marginBottom: '20px' }}>
            <button 
                onClick={handleScanClick}
                disabled={status === 'checking'}
                style={getButtonStyle()}
                onMouseOver={(e) => {
                    if (status === 'ready') {
                        e.target.style.backgroundColor = '#0056b3';
                    }
                }}
                onMouseOut={(e) => {
                    if (status === 'ready') {
                        e.target.style.backgroundColor = '#007bff';
                    }
                }}
            >
                {getButtonText()}
            </button>

            {error && (
                <div style={{
                    backgroundColor: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    color: '#721c24',
                    padding: '15px',
                    marginTop: '15px',
                    borderRadius: '4px'
                }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            {status === 'success' && (
                <div style={{
                    backgroundColor: '#d4edda',
                    border: '1px solid #c3e6cb',
                    color: '#155724',
                    padding: '10px 15px',
                    marginTop: '10px',
                    borderRadius: '4px',
                    fontSize: '14px'
                }}>
                    ✓ Scanner app is now open with {customerName}
                </div>
            )}
        </div>
    );
};

export default ScanButton;